import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  AlertCircle,
  Clock,
  RotateCcw,
  CheckCircle,
  XCircle,
  Calendar,
  Building2,
  Code,
} from 'lucide-react';
import { useSystemEvents } from '../../../hooks/platform-admin/useSystemEvents';
import { SystemEvent } from '../../../services/platform-admin/system-events.service';

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { getEventById, retryEvent, resolveEvent, ignoreEvent, isLoading, error } =
    useSystemEvents();

  const [event, setEvent] = useState<SystemEvent | null>(null);
  const [showResolveForm, setShowResolveForm] = useState(false);
  const [showIgnoreForm, setShowIgnoreForm] = useState(false);
  const [notes, setNotes] = useState('');
  const [actionError, setActionError] = useState('');

  const loadEvent = async () => {
    if (!id) return;
    const result = await getEventById(id);
    if (result) {
      setEvent(result);
    }
  };

  useEffect(() => {
    loadEvent();
  }, [id]);

  const handleRetry = async () => {
    if (!event) return;
    if (window.confirm('Retry this event now?')) {
      const result = await retryEvent(event.id);
      if (result) {
        await loadEvent();
      }
    }
  };

  const handleResolve = async () => {
    if (!event || !notes.trim()) {
      setActionError('Resolution notes are required');
      return;
    }

    const result = await resolveEvent(event.id, { notes });
    if (result) {
      setShowResolveForm(false);
      setNotes('');
      await loadEvent();
    }
  };

  const handleIgnore = async () => {
    if (!event || !notes.trim()) {
      setActionError('Ignore reason is required');
      return;
    }

    const result = await ignoreEvent(event.id, { notes });
    if (result) {
      setShowIgnoreForm(false);
      setNotes('');
      await loadEvent();
    }
  };

  const getSeverityBadge = (severity: SystemEvent['severity']) => {
    const colors = {
      LOW: 'bg-blue-100 text-blue-800',
      MEDIUM: 'bg-yellow-100 text-yellow-800',
      HIGH: 'bg-orange-100 text-orange-800',
      CRITICAL: 'bg-red-100 text-red-800',
    };

    return (
      <span className={`px-3 py-1 text-sm font-medium rounded-full ${colors[severity]}`}>
        {severity}
      </span>
    );
  };

  const getStatusBadge = (status: SystemEvent['status']) => {
    const colors = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      PROCESSING: 'bg-blue-100 text-blue-800',
      RESOLVED: 'bg-green-100 text-green-800',
      FAILED: 'bg-red-100 text-red-800',
      IGNORED: 'bg-gray-100 text-gray-800',
    };

    return (
      <span className={`px-3 py-1 text-sm font-medium rounded-full ${colors[status]}`}>
        {status}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  if (isLoading && !event) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading event...</div>
      </div>
    );
  }

  if (error && !event) {
    return (
      <div className="space-y-4">
        <Link
          to="/platform-admin/events"
          className="inline-flex items-center gap-2 text-orange-600 hover:text-orange-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Events
        </Link>
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="space-y-4">
        <Link
          to="/platform-admin/events"
          className="inline-flex items-center gap-2 text-orange-600 hover:text-orange-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Events
        </Link>
        <div className="text-gray-500">Event not found</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to="/platform-admin/events"
          className="inline-flex items-center gap-2 text-orange-600 hover:text-orange-700 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Events
        </Link>

        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-gradient-to-r from-orange-600 to-red-600 rounded-lg">
              <AlertCircle className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{event.operation}</h1>
              <div className="flex items-center gap-3 mt-2">
                {getStatusBadge(event.status)}
                {getSeverityBadge(event.severity)}
                <span className="text-sm text-gray-500">ID: {event.id}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            {event.status === 'PENDING' && event.retryCount < event.maxRetries && (
              <button
                onClick={handleRetry}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <RotateCcw className="h-4 w-4" />
                Retry Now
              </button>
            )}
            {(event.status === 'PENDING' || event.status === 'FAILED') && (
              <>
                <button
                  onClick={() => {
                    setShowResolveForm(true);
                    setShowIgnoreForm(false);
                    setActionError('');
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <CheckCircle className="h-4 w-4" />
                  Resolve
                </button>
                <button
                  onClick={() => {
                    setShowIgnoreForm(true);
                    setShowResolveForm(false);
                    setActionError('');
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <XCircle className="h-4 w-4" />
                  Ignore
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Resolve/Ignore Forms */}
      {(showResolveForm || showIgnoreForm) && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {showResolveForm ? 'Resolve Event' : 'Ignore Event'}
          </h3>

          {actionError && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-4">
              {actionError}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {showResolveForm ? 'Resolution Notes' : 'Ignore Reason'} *
              </label>
              <textarea
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value);
                  setActionError('');
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder={
                  showResolveForm
                    ? 'Describe how this issue was resolved...'
                    : 'Explain why this event can be ignored...'
                }
                rows={4}
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowResolveForm(false);
                  setShowIgnoreForm(false);
                  setNotes('');
                  setActionError('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={showResolveForm ? handleResolve : handleIgnore}
                className={`px-4 py-2 text-white rounded-lg transition-colors ${
                  showResolveForm
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-gray-600 hover:bg-gray-700'
                }`}
              >
                {showResolveForm ? 'Resolve Event' : 'Ignore Event'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Event Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg shadow border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Event Information</h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-start gap-3">
              <Code className="h-5 w-5 text-gray-400 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm text-gray-600">Event Type</div>
                <div className="text-base font-medium text-gray-900 mt-1">
                  {event.eventType.replace(/_/g, ' ')}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Code className="h-5 w-5 text-gray-400 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm text-gray-600">Source</div>
                <div className="text-base font-medium text-gray-900 mt-1">{event.source}</div>
              </div>
            </div>

            {event.organization && (
              <div className="flex items-start gap-3">
                <Building2 className="h-5 w-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm text-gray-600">Organization</div>
                  <div className="text-base font-medium text-gray-900 mt-1">
                    {event.organization.name}
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-gray-400 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm text-gray-600">Created</div>
                <div className="text-base font-medium text-gray-900 mt-1">
                  {formatDate(event.createdAt)}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-gray-400 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm text-gray-600">Retry Count</div>
                <div className="text-base font-medium text-gray-900 mt-1">
                  {event.retryCount} / {event.maxRetries}
                </div>
              </div>
            </div>

            {event.nextRetryAt && (
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm text-gray-600">Next Retry</div>
                  <div className="text-base font-medium text-gray-900 mt-1">
                    {formatDate(event.nextRetryAt)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Resolution Information */}
        {(event.resolvedAt || event.resolutionNotes) && (
          <div className="bg-white rounded-lg shadow border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Resolution</h2>
            </div>
            <div className="p-6 space-y-4">
              {event.resolvedAt && (
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm text-gray-600">Resolved At</div>
                    <div className="text-base font-medium text-gray-900 mt-1">
                      {formatDate(event.resolvedAt)}
                    </div>
                  </div>
                </div>
              )}

              {event.resolutionNotes && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="text-sm font-medium text-green-900 mb-1">Notes</div>
                  <div className="text-sm text-green-800">{event.resolutionNotes}</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Error Details */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Error Details</h2>
        </div>
        <div className="p-6 space-y-4">
          {event.errorMessage && (
            <div>
              <div className="text-sm font-medium text-gray-700 mb-2">Error Message</div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <pre className="text-sm text-red-800 whitespace-pre-wrap font-mono">
                  {event.errorMessage}
                </pre>
              </div>
            </div>
          )}

          {event.errorStack && (
            <div>
              <div className="text-sm font-medium text-gray-700 mb-2">Stack Trace</div>
              <details className="bg-gray-50 border border-gray-200 rounded-lg">
                <summary className="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 hover:bg-gray-100">
                  Click to expand stack trace
                </summary>
                <div className="px-4 py-3 border-t border-gray-200">
                  <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono overflow-x-auto">
                    {event.errorStack}
                  </pre>
                </div>
              </details>
            </div>
          )}

          {event.metadata && Object.keys(event.metadata).length > 0 && (
            <div>
              <div className="text-sm font-medium text-gray-700 mb-2">Metadata</div>
              <details className="bg-gray-50 border border-gray-200 rounded-lg">
                <summary className="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 hover:bg-gray-100">
                  Click to expand metadata
                </summary>
                <div className="px-4 py-3 border-t border-gray-200">
                  <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono overflow-x-auto">
                    {JSON.stringify(event.metadata, null, 2)}
                  </pre>
                </div>
              </details>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
